import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';

export default function UpgradePrompt({ 
  feature, 
  requiredPlan = "Pro", 
  currentPlan = "Free",
  description 
}) {
  const { t } = useTranslation();

  const planFeatures = {
    "Pro": [
      t('upgrade.feature1', 'Unbegrenzte Kunden & Projekte'),
      t('upgrade.feature2', 'Automatische Steuerberechnung'),
      t('upgrade.feature3', 'Erweiterte Finanzübersicht'),
      t('upgrade.feature4', 'Prioritäts-Support')
    ],
    "Basic": [
      t('upgrade.basicFeature1', 'Bis zu 2 Kunden'),
      t('upgrade.basicFeature2', 'Bis zu 4 Projekte'),
      t('upgrade.basicFeature3', 'Automatische Rechnungserstellung'),
      t('upgrade.basicFeature4', 'Dashboard-Übersicht')
    ]
  };

  return (
    <div className="flex items-center justify-center min-h-[70vh] p-4">
      <Card className="max-w-2xl w-full border-2 border-blue-200 shadow-xl">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <Lock className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {feature}
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {description || t('upgrade.defaultDescription', 'Dieses Feature ist nur in höheren Plänen verfügbar')}
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Current vs Required Plan */}
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('upgrade.currentPlan', 'Ihr aktueller Plan')}</p>
                <p className="text-xl font-bold text-gray-900">{currentPlan}</p>
              </div>
              <ArrowRight className="w-6 h-6 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">{t('upgrade.requiredPlan', 'Benötigter Plan')}</p>
                <p className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {requiredPlan}
                </p>
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="bg-blue-50 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">
                {t('upgrade.whatYouGet', 'Was Sie mit')} {requiredPlan} {t('upgrade.getPlan', 'erhalten')}:
              </h3>
            </div>
            <ul className="space-y-2">
              {(planFeatures[requiredPlan] || planFeatures["Pro"]).map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-green-500 font-bold mt-0.5">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pricing Info */}
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600">
              {requiredPlan === "Pro" && (
                <>
                  {t('upgrade.proPricing', 'Nur €19/Monat oder €190/Jahr (spare 17%)')}
                </>
              )}
              {requiredPlan === "Basic" && (
                <>
                  {t('upgrade.basicPricing', 'Nur €9/Monat oder €90/Jahr (spare 17%)')}
                </>
              )}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link to="/pricing" className="flex-1">
              <Button 
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-6 text-lg shadow-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                {t('upgrade.upgradeNow', 'Jetzt upgraden')}
              </Button>
            </Link>
            <Link to="/pricing" className="flex-1">
              <Button 
                variant="outline"
                className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-6"
              >
                {t('upgrade.viewPlans', 'Alle Pläne ansehen')}
              </Button>
            </Link>
          </div>

          {/* Footer Note */}
          <p className="text-center text-xs text-gray-500 pt-2">
            {t('upgrade.note', '7 Tage Geld-zurück-Garantie • Jederzeit kündbar')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
